// @flow
import React from "react";
import "./index.css";
import CardItem from "./CardItem";
import {
    type CardItemResultDTO,
    CardItemType,
    type CardItemTypeEnum,
} from "data/types";
import Constants, { type ConstantsEnum } from "data/constants";
import Status from "components/Status";
import NoData from "components/NoData";

type Props = {
    /** current fetch status */
    status: ConstantsEnum,
    /** all cards objects */
    cards?: Array<CardItemResultDTO>,
    /** which panes are currently toggled */
    toggles: Array<string>,
    /** callback when user clicks on a card
     * @param {CardItemResultDTO} item which card was clicked
     * @param {?SyntheticMouseEvent<*>} e click event details
     */
    onClick: (item: CardItemResultDTO, e: ?SyntheticMouseEvent<*>) => void,
    /** optional callback when user clicks on the three dots more
     * @param {CardItemResultDTO} item which card was clicked
     */
    onMoreClick?: (item: CardItemResultDTO) => void,
    /** optional function to validate whether to show the onMoreClick
     * @param {CardItemResultDTO} item which card
     * @default only for card.itemtype=Doc,Folder,Search,Task
     */
    showOnMoreClick?: (item: CardItemResultDTO) => boolean,
    /** which itemTypes to show */
    itemtypes?: Array<CardItemTypeEnum>,
    /** current filterText (or empty string if none) */
    filterText: string,
};

const CardList = ({
    status,
    cards,
    toggles,
    onClick,
    onMoreClick,
    showOnMoreClick,
    itemtypes,
    filterText,
}: Props) => {
    const _onMoreClick = (card: CardItemResultDTO) => {
        // onMoreClick defined at all?
        if (typeof onMoreClick === "function") {
            // custom evaluator defined?
            if (typeof showOnMoreClick === "function") {
                return showOnMoreClick(card)
                    ? (e) => e.stopPropagation() || onMoreClick(card)
                    : undefined;
            }
            // default evaluator (for backward compatibility)
            /* istanbul ignore else */
            if (
                [
                    CardItemType.Doc,
                    CardItemType.Folder,
                    CardItemType.Search,
                    CardItemType.Task,
                ].includes(card.itemtype)
            ) {
                return (e) => e.stopPropagation() || onMoreClick(card);
            }
        }
        // do not show onMore
        return undefined;
    };
    /* istanbul ignore else */
    if ([Constants.OK, Constants.REFRESH].includes(status) && cards != null) {
        const totalVisible = cards.filter(
            (item) =>
                toggles.includes(item.type) &&
                (itemtypes == null || itemtypes.includes(item.itemtype))
        ).length;
        if (totalVisible === 0) return <NoData locContext={toggles[0]} />;
        else
            return (
                <ul className="cards">
                    {cards &&
                        cards.map((item) => (
                            <CardItem
                                key={`${item.id}`}
                                {...item}
                                visible={
                                    toggles.includes(item.type) &&
                                    (itemtypes == null ||
                                        itemtypes.includes(item.itemtype))
                                }
                                filterText={filterText}
                                onClick={(e) => onClick(item, e)}
                                onMoreClick={_onMoreClick(item)}
                                dataTest={`cardItem-${item.uri}`}
                            />
                        ))}
                </ul>
            );
    }

    return <Status status={status} />;
};
CardList.displayName = "CardList";
export default CardList;
